import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllCategories, createCategory, updateCategory, deleteCategory } from "../../api/Api";

const CategoriesCrud = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState(null); // { _id, name }
  const [editCategoryName, setEditCategoryName] = useState("");

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getAllCategories();
      if (response && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Failed to load categories.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Category name cannot be empty.");
      return;
    }
    try {
      await createCategory({ name: newCategoryName });
      toast.success("Category created successfully!");
      setNewCategoryName("");
      fetchCategories();
    } catch (err) {
      console.error("Error creating category:", err);
      toast.error("Failed to create category.");
    }
  };

  const handleEditClick = (category) => {
    setEditingCategory(category);
    setEditCategoryName(category.name);
  };

  const handleUpdateCategory = async (id) => {
    if (!editCategoryName.trim()) {
      toast.error("Category name cannot be empty.");
      return;
    }
    try {
      await updateCategory(id, { name: editCategoryName });
      toast.success("Category updated successfully!");
      setEditingCategory(null);
      setEditCategoryName("");
      fetchCategories();
    } catch (err) {
      console.error("Error updating category:", err);
      toast.error("Failed to update category.");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await deleteCategory(id);
        toast.success("Category deleted successfully!");
        fetchCategories();
      } catch (err) {
        console.error("Error deleting category:", err);
        toast.error("Failed to delete category.");
      }
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading categories...</div>;
  }

  return (
    <div className="p-6 max-w-screen-xl mx-auto bg-white rounded-xl shadow-md min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header */}
      <h2 className="text-2xl font-bold mb-6 text-[#16bab1]">
        Manage Categories
      </h2>

      {/* Add New Category Form */}
      <div className="mb-6 p-6 border border-gray-100 rounded-xl bg-gray-50 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-700">Add New Category</h3>
        <div className="flex gap-4">
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Enter category name"
            className="flex-grow border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#16bab1] bg-white"
          />
          <button
            onClick={handleCreateCategory}
            className="bg-[#16bab1] text-white px-6 py-3 rounded-lg hover:bg-[#139a93] transition font-medium shadow-sm flex items-center gap-2"
          >
            <span className="text-lg font-bold">+</span> Add Category
          </button>
        </div>
      </div>

      {/* Categories List */}
      <div className="mt-8">
        <h3 className="text-xl font-bold mb-4 text-gray-800">Existing Categories</h3>
        {categories.length === 0 ? (
          <p className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
            No categories found. Start by adding one above.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full bg-white">
              <thead className="bg-[#16bab1] text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold text-sm w-16">S.No</th>
                  <th className="py-3 px-4 text-left font-semibold text-sm">Category Name</th>
                  <th className="py-3 px-4 text-center font-semibold text-sm w-48">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {categories.map((category, index) => (
                  <tr key={category._id} className="hover:bg-gray-50 transition">
                    <td className="py-3 px-4 text-gray-600 text-sm">{index + 1}</td>
                    <td className="py-3 px-4 text-sm">
                      {editingCategory && editingCategory._id === category._id ? (
                        <input
                          type="text"
                          value={editCategoryName}
                          onChange={(e) => setEditCategoryName(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#16bab1]"
                          autoFocus
                        />
                      ) : (
                        <span className="font-medium text-gray-800">{category.name}</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {editingCategory && editingCategory._id === category._id ? (
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleUpdateCategory(category._id)}
                            className="bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200 transition text-sm font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingCategory(null)}
                            className="bg-gray-100 text-gray-600 px-3 py-1 rounded hover:bg-gray-200 transition text-sm font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-center gap-3">
                          <button
                            onClick={() => handleEditClick(category)}
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm transition"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category._id)}
                            className="text-red-600 hover:text-red-800 font-medium text-sm transition"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesCrud;
